if (!customElements.get('product-media-swiper')) {
  customElements.define(
    'product-media-swiper',
    class ProductMediaSwiper extends HTMLElement {
      connectedCallback() {
        if (this.mainSwiper || typeof Swiper === 'undefined') return;

        this.elements = {
          main: this.querySelector('[data-gallery-main]'),
          mainList: this.querySelector('[data-gallery-main-list]'),
          thumbs: this.querySelector('[data-gallery-thumbs]'),
          thumbsList: this.querySelector('[data-gallery-thumbs-list]'),
        };

        if (this.elements.thumbs) {
          this.syncThumbnails();
          this.thumbsSwiper = new Swiper(this.elements.thumbs, {
            direction: 'horizontal',
            spaceBetween: this.getThumbnailLayout().gap,
            watchSlidesProgress: true,
            watchOverflow: true,
            freeMode: { enabled: true, sticky: false },
            // Wheel scrolling is switched on once a thumbnail is clicked, and off again on a click elsewhere.
            mousewheel: { enabled: false, forceToAxis: true, releaseOnEdges: true },
            // A whole number of square thumbnails fill the row, so none is cut off at the edge
            // and the "+N" lands on the last one. See getThumbnailLayout().
            slidesPerView: this.getThumbnailLayout().perView,
            on: {
              afterInit: () => this.updateMoreOverlay(),
              setTranslate: () => this.updateMoreOverlay(),
              resize: (swiper) => {
                const { perView, gap } = this.getThumbnailLayout();
                if (swiper.params.slidesPerView !== perView || swiper.params.spaceBetween !== gap) {
                  swiper.params.slidesPerView = perView;
                  swiper.params.spaceBetween = gap;
                  swiper.update();
                }
                this.updateMoreOverlay();
              },
              update: () => this.updateMoreOverlay(),
            },
          });

          this.elements.thumbsList.addEventListener('click', this.onThumbnailClick.bind(this));
          this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
          document.addEventListener('pointerdown', this.onDocumentPointerDown);

          // Layout can still shift while the page's stylesheets finish loading.
          if (document.readyState !== 'complete') {
            window.addEventListener('load', () => this.thumbsSwiper?.update(), { once: true });
          }
        }

        const activeSlide = this.elements.mainList.querySelector('.is-active');
        const slides = Array.from(this.elements.mainList.children);

        this.mainSwiper = new Swiper(this.elements.main, {
          slidesPerView: 1,
          speed: 400,
          initialSlide: Math.max(slides.indexOf(activeSlide), 0),
          watchOverflow: true,
          noSwipingSelector: 'model-viewer, input, select, textarea',
          keyboard: { enabled: true, onlyInViewport: true },
          on: {
            slideChange: () => this.onSlideChange(),
          },
        });

        this.onSlideChange();
      }

      disconnectedCallback() {
        if (this.onDocumentPointerDown) document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        this.mainSwiper?.destroy(true, true);
        this.thumbsSwiper?.destroy(true, true);
        this.mainSwiper = null;
        this.thumbsSwiper = null;
      }

      // Mobile: five per row. Tablet: as many as fit at 72px or more. Desktop: the count that brings
      // them closest to the Figma 67px. Sizes and gaps match --gallery-thumb-size / --gallery-thumb-gap.
      getThumbnailLayout() {
        if (!window.matchMedia('(min-width: 750px)').matches) return { perView: 5, gap: 10 };

        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const size = isDesktop ? 67 : 72;
        const gap = isDesktop ? 11 : 10;
        const thumbs = this.elements.thumbs;
        const style = getComputedStyle(thumbs);
        const width = thumbs.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const count = (isDesktop ? Math.round : Math.floor)((width + gap) / (size + gap));
        return { perView: Math.max(count, 1), gap };
      }

      onThumbnailClick(event) {
        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail) return;

        this.setActiveMedia(thumbnail.dataset.target, false);
        if (thumbnail.classList.contains('has-more')) thumbnail.querySelector('button').focus();
        this.setThumbnailWheelScroll(true);
      }

      onDocumentPointerDown(event) {
        if (!this.elements.thumbs.contains(event.target)) this.setThumbnailWheelScroll(false);
      }

      setThumbnailWheelScroll(enabled) {
        const mousewheel = this.thumbsSwiper?.mousewheel;
        if (!mousewheel || mousewheel.enabled === enabled) return;

        enabled ? mousewheel.enable() : mousewheel.disable();
      }

      onSlideChange() {
        const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
        if (!activeSlide) return;

        window.pauseAllMedia?.();
        this.mainSwiper.slides.forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));
        this.setActiveThumbnail(activeSlide.dataset.mediaId);
      }

      // Also called by product-info.js when the selected variant changes.
      setActiveMedia(mediaId, prepend) {
        if (!this.mainSwiper) return;

        const slides = Array.from(this.elements.mainList.querySelectorAll('[data-media-id]'));
        const activeSlide = slides.find((slide) => slide.dataset.mediaId === mediaId) || slides[0];
        if (!activeSlide) return;

        if (prepend) {
          if (this.elements.mainList.firstElementChild !== activeSlide) this.elements.mainList.prepend(activeSlide);
          // product-info.js may have added/removed slides, so re-read the DOM.
          this.syncThumbnails();
          this.mainSwiper.update();
          this.thumbsSwiper?.update();
        }

        const index = this.mainSwiper.slides.indexOf(activeSlide);
        if (index === this.mainSwiper.activeIndex) {
          this.onSlideChange();
        } else {
          this.mainSwiper.slideTo(index, prepend ? 0 : undefined);
        }
      }

      setActiveThumbnail(mediaId) {
        if (!this.thumbsSwiper) return;

        let activeThumbnail;
        this.thumbsSwiper.slides.forEach((thumbnail) => {
          const isActive = thumbnail.dataset.target === mediaId;
          thumbnail.classList.toggle('is-active', isActive);
          const button = thumbnail.querySelector('button');
          isActive ? button.setAttribute('aria-current', 'true') : button.removeAttribute('aria-current');
          if (isActive) activeThumbnail = thumbnail;
        });

        if (activeThumbnail) this.scrollThumbnailIntoView(activeThumbnail);
      }

      // Keeps the active thumbnail visible, with the next one also visible when there is one,
      // so selecting the "+N" thumbnail reveals the hidden ones.
      scrollThumbnailIntoView(thumbnail) {
        const slides = this.thumbsSwiper.slides;
        const index = slides.indexOf(thumbnail);
        const visibleIndexes = this.getFullyVisibleThumbnailIndexes();
        if (!visibleIndexes.length) return;

        const first = visibleIndexes[0];
        const last = visibleIndexes[visibleIndexes.length - 1];
        const target = Math.min(index + 1, slides.length - 1);

        if (index < first) {
          this.thumbsSwiper.slideTo(index);
        } else if (target > last) {
          this.thumbsSwiper.slideTo(Math.max(target - visibleIndexes.length + 1, 0));
        }
      }

      // Worked out from layout offsets and the target translate (not Swiper's visibility classes, which are
      // thrown off by the container padding and lag behind on first load).
      getFullyVisibleThumbnailIndexes() {
        const swiper = this.thumbsSwiper;
        const isHorizontal = swiper.isHorizontal();
        const start = -swiper.translate;
        const end = start + swiper.size;
        const tolerance = 1;

        return swiper.slides.reduce((indexes, slide, index) => {
          const offset = isHorizontal ? slide.offsetLeft : slide.offsetTop;
          const size = isHorizontal ? slide.offsetWidth : slide.offsetHeight;
          if (size && offset >= start - tolerance && offset + size <= end + tolerance) indexes.push(index);
          return indexes;
        }, []);
      }

      // Shows "+N" over the last fully visible thumbnail when more thumbnails follow it.
      updateMoreOverlay() {
        if (!this.thumbsSwiper) return;

        const slides = this.thumbsSwiper.slides;
        slides.forEach((slide) => slide.classList.remove('has-more'));
        const lastVisibleIndex = this.getFullyVisibleThumbnailIndexes().pop() ?? -1;

        const remaining = slides.length - 1 - lastVisibleIndex;
        if (lastVisibleIndex < 0 || remaining <= 0) return;

        const slide = slides[lastVisibleIndex];
        slide.classList.add('has-more');
        slide.querySelector('[data-more-count]').textContent = `+${remaining}`;
      }

      // Matches the thumbnails to the main slides: same order, and hides thumbnails without a slide.
      syncThumbnails() {
        if (!this.elements.thumbsList) return;

        const mediaIds = Array.from(this.elements.mainList.querySelectorAll('[data-media-id]')).map(
          (slide) => slide.dataset.mediaId
        );
        const thumbnails = Array.from(this.elements.thumbsList.querySelectorAll('[data-target]'));

        thumbnails.forEach((thumbnail) => {
          const isVisible = mediaIds.includes(thumbnail.dataset.target);
          thumbnail.hidden = !isVisible;
          thumbnail.classList.toggle('swiper-slide', isVisible);
        });

        mediaIds.forEach((mediaId) => {
          const thumbnail = thumbnails.find((item) => item.dataset.target === mediaId);
          if (thumbnail) this.elements.thumbsList.append(thumbnail);
        });

        this.classList.toggle('product-gallery--single', mediaIds.length <= 1);
      }
    }
  );
}
